import { ReactNode } from "react";
import { Outlet, useNavigate, useOutletContext } from "react-router-dom";
import { Bell, Brain, IdCard, Palette, Sparkles, User } from "lucide-react";
import { NextGenLayoutContext } from "~/components/ManifoldNextGenLayout";
import useNextGenHeader from "~/contexts/nextgen-header";
import { usePageContext } from "~/contexts/page-context";
import { SectionNavGroupLabel } from "~/pages/manifold-nextgen/components/SectionNavGroupLabel";
import { SectionNavLink } from "~/pages/manifold-nextgen/components/SectionNavLink";
import { SectionNavPlaceholder } from "~/pages/manifold-nextgen/components/SectionNavPlaceholder";
import { SectionSidebar } from "~/pages/manifold-nextgen/components/SectionSidebar";

// A nav row with no `path` is a feature that belongs in the profile's shape but
// has no page behind it yet, and renders as a disabled row.
interface NavItem {
  label: string;
  icon: ReactNode;
  path?: string;
}

const ACCOUNT_ITEMS: NavItem[] = [
  { label: "Account", path: "account", icon: <User className="size-4" /> },
  { label: "Appearance", path: "appearance", icon: <Palette className="size-4" /> },
  { label: "Notifications", icon: <Bell className="size-4" /> },
];

// What the agents carry on this user's behalf, as opposed to how the app treats
// the person using it.
const AI_ITEMS: NavItem[] = [
  {
    label: "Personalization",
    path: "personalization",
    icon: <IdCard className="size-4" />,
  },
  { label: "Memories", path: "memories", icon: <Brain className="size-4" /> },
  { label: "Skills", path: "skills", icon: <Sparkles className="size-4" /> },
];

const NavItems = ({ items }: { items: NavItem[] }) => (
  <>
    {items.map(({ label, path, icon }) =>
      path === undefined ? (
        <SectionNavPlaceholder key={label} icon={icon} label={label} />
      ) : (
        <SectionNavLink key={label} to={path} end icon={icon} label={label} />
      )
    )}
  </>
);

/**
 * Shell for the user's own profile: the section sidebar plus whichever tab the
 * route resolves to. Forwards the layout context so tabs can reach the
 * dark-mode / classic-look controls.
 */
export const UserProfile = () => {
  const navigate = useNavigate();
  const layoutContext = useOutletContext<NextGenLayoutContext>();

  useNextGenHeader({ title: "Profile", onBack: () => navigate(-1) });
  usePageContext({ page: "profile" });

  return (
    <div className="flex h-full overflow-hidden">
      <SectionSidebar ariaLabel="Profile sections">
        <div className="flex flex-col gap-1 px-4 py-3">
          <NavItems items={ACCOUNT_ITEMS} />

          <SectionNavGroupLabel className="mt-3 px-3">AI</SectionNavGroupLabel>
          <NavItems items={AI_ITEMS} />
        </div>
      </SectionSidebar>

      <div className="flex-1 overflow-y-auto p-6">
        <Outlet context={layoutContext} />
      </div>
    </div>
  );
};
